import React from 'react';
import { get, findIndex, cloneDeep, map, isNil } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

// local dependencies
import { authFlowCallerIdentificationStep as AuthFlowCallerIdentificationStepView } from 'ui/views/widgets/authFlowCallerIdentificationStep';
import { AuthFlowCallerIdentificationStepService } from 'services/authFlowCallerIdentificationStep';
import { SERVICE_RESPONSE_STATUS, CALL_ACTIVITY_STATE } from 'constants/common';
import { REDUCER } from 'constants/entity-names';
import { getCallerTypeNameFromValue } from 'resolvers/callStateEntities';
import { updateCallState } from 'store/actions/callState';
import { isOutbound } from 'resolvers/callStateEntities';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowCallerIdentificationStep } from 'ui/containers/AuthFlowCallerIdentificationStep';
 *
 * // render
 * <AuthFlowCallerIdentificationStep propName='propValue'/>
 */
class _AuthFlowCallerIdentificationStep extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        log.debug( 'AuthFlowCallerIdentificationStep.constructor()' );

        super( props );

        // bind events and methods
        this.handleNextActionClick = this.handleNextActionClick.bind( this );
        this.handleTabChange = this.handleTabChange.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            currentTab: undefined,
        };

        // create service instance
        this.authFlowCallerIdentificationStepService = new AuthFlowCallerIdentificationStepService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'AuthFlowCallerIdentificationStep.fetchComponentData()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationStepService.cancel();

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationStepService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                currentTab: this.getActiveTab( data ),
            } );
        }
    }

    /**
     * @desc Get active tab (caller type).
     */
    getActiveTab( componentData ) {
        log.debug( 'AuthFlowCallerIdentificationStep.getActiveTab()' );

        // check if `callerInfo` in the ongoing call data has `type`
        if( get( this.props, 'callState.callerInfo.type' ) ) {

            // get caller type name based on value
            const callerTypeName = getCallerTypeNameFromValue( get( this.props, 'callState.callerInfo.type' ) );
            
            // find active tab with `value.callerType` property
            const activeTabIndex = findIndex( get( componentData, 'tabs', [] ), tab => {
                return get( tab, 'value.callerType' ) === callerTypeName;
            } );

            // return active step if found
            if( -1 !== activeTabIndex ) {
                return activeTabIndex + 1;
            }
        }

        // return `undefined` when no tab is selected
        return undefined;
    }

    /**
     * @desc Handle tab change.
     */
    handleTabChange( selectedTab ) {
        log.debug( 'AuthFlowCallerIdentificationStep.handleTabChange()' );

        // move one step forward in authorization flow
        this.setState( {
            currentTab: selectedTab,
        } );
    }

    /**
     * @desc Handle next action of the step.
     */
    handleNextActionClick( payload ) {
        log.debug( 'AuthFlowCallerIdentificationStep.handleNextActionClick()' );

        // update call state in the redux store
        this.props.updateCallState( {
            callState: CALL_ACTIVITY_STATE.VERIFYING,
        } );

        // move one step forward in authorization flow
        this.props.moveForward( payload );

        // this.props.history.push( {
        //     pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
        //     search: queryString.stringify( {
        //         step: 2
        //     } ),
        // } );
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.authFlowCallerIdentificationStepService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {

        // construct tabs with disability
        const currentTab = get( this.state, 'currentTab' );
        let tabs = cloneDeep( get( this.state, 'componentData.tabs', [] ) );

        if( isOutbound() && ! isNil( currentTab ) ) {
            tabs = map( tabs, ( _tab, index ) => ( {
                ..._tab,
                disabled: currentTab - 1 !== index,
            } ) );
        }
        
        return (
            <AuthFlowCallerIdentificationStepView
                callState={ this.props.callState }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                currentTab={ currentTab }
                onTabChange={ this.handleTabChange }
                tabs={ tabs }
                navActions={ get( this.state, 'componentData.navActions', [] ) }
                onNextActionClick={ this.handleNextActionClick }
            />
        );
    }
}

// set display name
_AuthFlowCallerIdentificationStep.displayName = '_AuthFlowCallerIdentificationStep';

// set default props
_AuthFlowCallerIdentificationStep.defaultProps = {};

// prop types
_AuthFlowCallerIdentificationStep.propTypes = {};

/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
        appContext: state[ REDUCER.APP_CONTEXT ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const AuthFlowCallerIdentificationStep = connect( mapStateToProps, mapDispatchToProps )( _AuthFlowCallerIdentificationStep );
